// app/frontend/pages/Conversation.tsx

import { useState, useCallback, useEffect, useRef } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import MainLayout from '@/components/Layout/MainLayout'
import { FilterProvider, useFilter, type FolderOption, type FilterOption, type AssignmentOption } from '@/contexts/FilterContext'
import { useInboxChannel, useConversationChannel } from '@/hooks/useCable'
import { useStaleRefresh } from '@/hooks/useStaleRefresh'
import { usePaginatedConversations } from '@/hooks/usePaginatedConversations'
import { useRouteParams } from '@/hooks/useRoutes'
import { useMessageTranslation } from '@/hooks/useMessageTranslation'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useConversationDraft } from '@/hooks/useConversationDraft'
import routes from '@/routeHelpers'
import type { InboxMessage, ConversationMessage } from '@/lib/cable'
import { mergeConversationUpdate } from '@/lib/conversationListUpdate'
import type {
  User,
  Account,
  Inbox,
  ConversationData,
  Message,
  ConversationListItem as ConversationListItemType
} from '@/types/conversation'

// Components
import { ConversationSidebar } from '@/components/inbox/ConversationSidebar'
import { ConversationHeader } from '@/components/inbox/ConversationHeader'
import { LiveTranslateHeader } from '@/components/inbox/LiveTranslateHeader'
import { MessageList } from '@/components/inbox/MessageList'
import { MessageComposer } from '@/components/inbox/MessageComposer'
import type { Suggestion, AiDraftSuggestion } from '@/components/inbox/SuggestionBar'
import { useConversationSuggestions } from '@/hooks/useConversationSuggestions'
import { TypingIndicator } from '@/components/inbox/TypingIndicator'
import { VisitorDetailDrawer } from '@/components/inbox/VisitorDetailDrawer'

interface AssignableAgent {
  id: string
  name: string
}

interface ConversationProps {
  current_user: User
  current_account: Account
  inbox: Inbox
  conversation: ConversationData
  messages: Message[]
  conversations: ConversationListItemType[]
  conversations_next_cursor: string | null
  team_members: AssignableAgent[]
  current_filter?: FilterOption
  current_folder?: FolderOption
  current_assignment?: AssignmentOption
}

// Status-only mutations redirect back to the same conversation page. Requesting
// only the conversation prop (and flash) lets Inertia skip re-serializing the
// message thread, the sidebar list, and the heavy shared sidebar data — so
// resolving/reopening no longer triggers a full-page loading indicator.
const STATUS_RELOAD = {
  only: ['conversation', 'flash'],
  preserveScroll: true,
  preserveState: true,
}

// Minimal HTML attribute escape — sufficient because we only inject into
// quoted attribute values. Backend Loofah scrub is the authoritative pass;
// this just ensures the string we send parses to what we intended.
function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function ConversationContent({
  current_user,
  current_account,
  inbox,
  conversation,
  messages: initialMessages,
  conversations: initialConversations,
  conversations_next_cursor,
  team_members
}: Omit<ConversationProps, 'current_filter' | 'current_folder' | 'current_assignment'>) {
  const { withAccount, withInbox } = useRouteParams()
  const { currentFilter, currentFolder, currentAssignment } = useFilter()
  const { flash } = usePage<{ flash: { notice?: string } }>().props

  // Local state
  const { draft: newMessage, setDraft: setNewMessage, clearDraft } = useConversationDraft(conversation.id)
  // Tracks the conversation actually on screen, kept in sync during render (not
  // a passive effect) so a send's onFinish can tell whether the agent is still
  // looking at the conversation it was sent from before touching shared state
  // like the picked canned/AI attribution or the pending attachment.
  const activeConversationId = useRef(conversation.id)
  activeConversationId.current = conversation.id
  const [isSending, setIsSending] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const {
    conversations: conversationsList,
    setConversations: setConversationsList,
    nextCursor,
    appendPage
  } = usePaginatedConversations(initialConversations, conversations_next_cursor)
  const [messages, setMessages] = useState(initialMessages)
  const [typingIndicator, setTypingIndicator] = useState<{ name: string } | null>(null)
  const [isTogglingLiveTranslate, setIsTogglingLiveTranslate] = useState(false)
  const [visitorDrawerOpen, setVisitorDrawerOpen] = useState(false)

  // File upload hook
  const {
    pendingFile,
    handleFileSelect,
    handleFileRemove,
    uploadFile,
    clearFile
  } = useFileUpload()

  // LiveTranslate is enabled when detected_language is set
  const liveTranslateEnabled = !!conversation.detected_language

  // Translation hook
  const {
    translations,
    visitorLanguage,
    translateOnSend,
    setTranslateOnSend,
    handleTranslate,
    autoTranslateMessage
  } = useMessageTranslation({
    accountSlug: current_account.slug,
    conversationId: conversation.id,
    initialMessages,
    liveTranslateEnabled,
    detectedLanguage: conversation.detected_language,
    detectedLanguageName: conversation.detected_language_name
  })

  // Permanently flush read state into conversationsList when switching conversations.
  useEffect(() => {
    setConversationsList(prev =>
      prev.map(c => c.id === conversation.id && c.unread ? { ...c, unread: false } : c)
    )
  }, [conversation.id])

  // Sync messages when props change. Translation state is reset inside
  // useMessageTranslation when the conversation changes (BC-103).
  useEffect(() => {
    setMessages(initialMessages)
  }, [initialMessages])

  // Auto-translate visitor messages when live translate is enabled
  // Note: Only depend on messages and liveTranslateEnabled to avoid race conditions
  // from translations state updates. Defensive checks in autoTranslateMessage prevent duplicates.
  useEffect(() => {
    if (!liveTranslateEnabled) return

    // Find visitor messages that haven't been translated yet
    const untranslatedVisitorMessages = messages.filter(
      msg => msg.sender_type === 'Visitor' &&
             msg.message_type !== 'system' &&
             !translations[msg.id]?.translated &&
             !translations[msg.id]?.loading
    )

    // Auto-translate each one
    untranslatedVisitorMessages.forEach(msg => {
      autoTranslateMessage(msg.id)
    })
  }, [messages, liveTranslateEnabled, autoTranslateMessage])

  // Inbox channel handler
  const handleInboxMessage = useCallback((data: InboxMessage) => {
    if (data.type === 'new_conversation') {
      setConversationsList(prev => {
        if (prev.some(c => c.id === data.conversation.id)) return prev
        return [{ ...data.conversation, unread: true }, ...prev]
      })
    } else if (data.type === 'conversation_updated') {
      // A visitor who identifies mid-conversation renames the open thread, and
      // a merge can hand it to a different visitor entirely. The header,
      // breadcrumb and detail drawer all read the Inertia prop, so pull a fresh
      // copy of just the conversation rather than patching each. An identity
      // carrying only an external id moves `identified` without touching the
      // display name, so that counts too.
      const openVisitor = conversation.visitor
      const updatedVisitor = data.conversation.visitor
      if (
        data.conversation.id === conversation.id &&
        (updatedVisitor.id !== openVisitor.id ||
          updatedVisitor.name !== openVisitor.name ||
          updatedVisitor.email !== openVisitor.email ||
          updatedVisitor.identified !== openVisitor.identified)
      ) {
        router.reload({ only: ['conversation'] })
      }

      setConversationsList(prev => {
        const index = prev.findIndex(c => c.id === data.conversation.id)
        if (index === -1) return prev
        const updated = [...prev]
        updated[index] = mergeConversationUpdate(
          prev[index],
          data.conversation,
          data.conversation.id === conversation.id
        )
        updated.sort((a, b) => {
          const aTime = a.last_message_at || a.created_at
          const bTime = b.last_message_at || b.created_at
          return new Date(bTime).getTime() - new Date(aTime).getTime()
        })
        return updated
      })
    }
  }, [conversation.id, conversation.visitor])

  // Conversation channel handler
  const handleConversationMessage = useCallback((data: ConversationMessage) => {
    if (data.type === 'new_message') {
      setMessages(prev => {
        if (prev.some(m => m.id === data.message.id)) return prev
        return [...prev, {
          id: data.message.id,
          content: data.message.content,
          blurred: data.message.blurred,
          message_type: data.message.message_type,
          sender_type: data.message.sender_type,
          sender_name: data.message.sender_name || (data.message.message_type === 'system' ? null : 'Visitor'),
          sender_avatar_url: data.message.sender_avatar_url,
          created_at: data.message.created_at,
          source_language: data.message.source_language,
          translation: data.message.translation,
          metadata: data.message.metadata,
          attachment: data.message.attachment
        }]
      })
      // Clear typing indicator when new message arrives from the same sender
      if (data.message.sender_type === 'Visitor') {
        setTypingIndicator(null)
      }
    } else if (data.type === 'typing') {
      // Only show typing indicator for visitors (not our own typing)
      if (data.sender.type === 'visitor') {
        if (data.is_typing) {
          setTypingIndicator({ name: data.sender.name })
        } else {
          setTypingIndicator(null)
        }
      }
    }
  }, [])

  // Subscribe to channels
  useInboxChannel(current_account?.slug, handleInboxMessage)
  useConversationChannel(conversation.id, handleConversationMessage)

  // BC-18 — suggested answers + AI draft chips
  const { suggestions, allSuggestions, aiDraft, clearDraft: clearAiDraft } = useConversationSuggestions(current_account?.slug, conversation.id)
  const [pickedCannedAnswerId, setPickedCannedAnswerId] = useState<string | null>(null)
  const [pickedAiDraftId, setPickedAiDraftId] = useState<string | null>(null)

  // Chip click semantics live inside MessageComposer (it replaces the draft,
  // auto-fills {{*url*}} placeholders from clipboard, and selects the next
  // placeholder). The parent only tracks which canned/draft was used so the
  // backend can attribute the send.
  const handlePickSuggestion = useCallback((s: Suggestion) => {
    setPickedCannedAnswerId(s.id)
    setPickedAiDraftId(null)
  }, [])

  const handlePickDraft = useCallback((d: AiDraftSuggestion) => {
    setPickedAiDraftId(d.id)
    setPickedCannedAnswerId(d.based_on_canned_id)
    clearAiDraft()
  }, [clearAiDraft])

  // The rest of the composer belongs to its conversation too: a pending
  // attachment or a canned/AI attribution must never follow the agent into the
  // next conversation.
  useEffect(() => {
    clearFile()
    setPickedCannedAnswerId(null)
    setPickedAiDraftId(null)
  }, [conversation.id, clearFile])

  // Refresh data when returning to tab after 5+ minutes
  useStaleRefresh()

  // Send message handler
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    // Can send if there's text OR a pending file
    if ((!newMessage.trim() && !pendingFile) || isSending || isTranslating) return

    let contentToSend = newMessage
    let translationMeta: { original_content?: string; source_language?: string; target_language?: string } = {}

    if (translateOnSend && visitorLanguage && newMessage.trim()) {
      setIsTranslating(true)
      try {
        const response = await fetch(`/${current_account.slug}/translations/text`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
          },
          body: JSON.stringify({
            text: newMessage,
            target_language: visitorLanguage.code
          })
        })
        const data = await response.json()
        if (response.ok && !data.skipped) {
          contentToSend = data.translated
          translationMeta = {
            original_content: newMessage,
            source_language: data.source_language,
            target_language: data.target_language
          }
        }
      } catch (error) {
        console.error('Translation failed, sending original:', error)
      }
      setIsTranslating(false)
    }

    // Upload file if pending
    let attachmentSignedId: string | null = null
    if (pendingFile && pendingFile.status !== 'error') {
      attachmentSignedId = await uploadFile()
      if (!attachmentSignedId && pendingFile) {
        // Upload failed, don't send message
        return
      }
    }

    const sentConversationId = conversation.id
    setIsSending(true)
    router.post(routes.conversations.createMessage.path(withInbox({ id: conversation.id })), {
      content: contentToSend,
      ...translationMeta,
      ...(pickedCannedAnswerId && { canned_answer_id: pickedCannedAnswerId }),
      ...(pickedAiDraftId && { ai_draft_id: pickedAiDraftId }),
      ...(attachmentSignedId && { attachment_signed_id: attachmentSignedId })
    }, {
      onFinish: () => {
        setIsSending(false)
        clearDraft(sentConversationId)
        // Only the conversation that was actually sent from owns this cleanup —
        // if the agent has since moved on, the picked suggestion and attachment
        // they're now working with belong to the conversation on screen, not
        // the one that just finished sending.
        if (activeConversationId.current === sentConversationId) {
          setPickedCannedAnswerId(null)
          setPickedAiDraftId(null)
          clearFile()
        }
      }
    })
  }

  // Sends a Giphy GIF as a standalone message — content is an <img> tag
  // pointing at the Giphy CDN URL. Doesn't consume the in-progress draft.
  // URL host is enforced by MessageHtmlSanitizer (Giphy only); alt comes
  // from Giphy's "title" so we escape it for safe insertion into the
  // attribute value before the server-side scrub runs.
  const handleSendGif = (url: string, alt: string) => {
    if (isSending) return
    setIsSending(true)
    router.post(routes.conversations.createMessage.path(withInbox({ id: conversation.id })), {
      content: `<p><img src="${escapeAttr(url)}" alt="${escapeAttr(alt)}"></p>`
    }, {
      onFinish: () => setIsSending(false)
    })
  }

  const handleCloseConversation = () => {
    router.patch(routes.conversations.close.path(withInbox({ id: conversation.id })), {}, STATUS_RELOAD)
  }

  const handleReopenConversation = () => {
    router.patch(routes.conversations.reopen.path(withInbox({ id: conversation.id })), {}, STATUS_RELOAD)
  }

  // Send typing indicator to server
  const handleTyping = useCallback((isTyping: boolean) => {
    fetch(routes.conversations.typing.path(withInbox({ id: conversation.id })), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ is_typing: isTyping })
    }).catch(() => {
      // Silently ignore typing indicator errors
    })
  }, [conversation.id, withInbox])

  const handleUpdateSubject = (subject: string) => {
    router.patch(routes.conversations.updateSubject.path(withInbox({ id: conversation.id })), { subject }, STATUS_RELOAD)
  }

  const handleArchive = () => {
    router.patch(routes.conversations.archive.path(withInbox({ id: conversation.id })))
  }

  const handleMarkAsSpam = () => {
    router.patch(routes.conversations.markAsSpam.path(withInbox({ id: conversation.id })))
  }

  const handleMarkAsUnread = () => {
    router.patch(routes.conversations.markAsUnread.path(withInbox({ id: conversation.id })), {}, STATUS_RELOAD)
  }

  const handleDelete = () => {
    router.delete(routes.conversations.destroy.path(withInbox({ id: conversation.id })))
  }

  const handleAssign = (userId: string | null) => {
    router.patch(routes.conversations.assign.path(withInbox({ id: conversation.id })), {
      assignee_id: userId || ''
    }, STATUS_RELOAD)
  }

  const handleSendTranscript = (email: string) => {
    router.post(
      routes.conversations.sendTranscript.path(withInbox({ id: conversation.id })),
      { email },
      STATUS_RELOAD
    )
  }

  const handleToggleLiveTranslate = () => {
    setIsTogglingLiveTranslate(true)
    router.patch(
      routes.conversations.toggleLiveTranslate.path(withInbox({ id: conversation.id })),
      { language_code: conversation.auto_detected_language },
      {
        ...STATUS_RELOAD,
        onFinish: () => setIsTogglingLiveTranslate(false)
      }
    )
  }

  const getConversationPath = (convId: string) =>
    routes.conversations.show.path(withInbox({ id: convId }))

  return (
    <>
      <Head title={`Chat with ${conversation.visitor.name}`} />
      <MainLayout
        user={current_user}
        breadcrumbs={[
          { label: inbox.name, href: routes.inbox.show.path(withAccount()) },
          { label: conversation.visitor.name }
        ]}
      >
        <div className="flex flex-1 min-h-0">
          <ConversationSidebar
            conversations={conversationsList.map(c =>
              c.id === conversation.id && c.unread ? { ...c, unread: false } : c
            )}
            currentConversationId={conversation.id}
            currentFilter={currentFilter}
            currentFolder={currentFolder}
            currentAssignment={currentAssignment}
            currentUserId={current_user.id}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            getConversationPath={getConversationPath}
            nextCursor={nextCursor}
            onLoadMore={appendPage}
          />

          <div className="flex flex-1 flex-col min-h-0 min-w-0">
            <ConversationHeader
              visitor={conversation.visitor}
              status={conversation.status}
              subject={conversation.subject}
              displaySubject={conversation.display_subject}
              assignee={conversation.assignee}
              csatResponse={conversation.csat_response}
              teamMembers={team_members}
              currentUserId={current_user.id}
              onClose={handleCloseConversation}
              onReopen={handleReopenConversation}
              onUpdateSubject={handleUpdateSubject}
              onArchive={handleArchive}
              onMarkAsSpam={handleMarkAsSpam}
              onMarkAsUnread={handleMarkAsUnread}
              onDelete={handleDelete}
              onAssign={handleAssign}
              onSendTranscript={handleSendTranscript}
              onToggleVisitorDetails={() => setVisitorDrawerOpen(true)}
            />

            <LiveTranslateHeader
              detectedLanguage={conversation.detected_language ?? null}
              detectedLanguageName={conversation.detected_language_name ?? null}
              liveTranslateEnabled={liveTranslateEnabled}
              autoDetectedLanguage={conversation.auto_detected_language ?? null}
              autoDetectedLanguageName={conversation.auto_detected_language_name ?? null}
              onToggle={handleToggleLiveTranslate}
              isLoading={isTogglingLiveTranslate}
            />

            {flash?.notice && (
              <div className="mx-4 mt-4 p-3 rounded-lg bg-primary/10 text-sm text-primary">
                {flash.notice}
              </div>
            )}

            <MessageList
              messages={messages}
              translations={translations}
              onTranslate={handleTranslate}
              accountWebsite={current_account.website}
            />

            {typingIndicator && (
              <TypingIndicator senderName={typingIndicator.name} />
            )}

            <MessageComposer
              value={newMessage}
              onChange={setNewMessage}
              onSubmit={handleSendMessage}
              onTyping={handleTyping}
              isSending={isSending}
              isTranslating={isTranslating}
              isDisabled={conversation.status === 'closed'}
              visitorLanguage={visitorLanguage}
              translateOnSend={translateOnSend}
              onToggleTranslate={() => setTranslateOnSend(!translateOnSend)}
              onReopen={handleReopenConversation}
              pendingFile={pendingFile}
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              suggestions={suggestions}
              allSuggestions={allSuggestions}
              aiDraft={aiDraft}
              onPickSuggestion={handlePickSuggestion}
              onPickDraft={handlePickDraft}
              onSendGif={handleSendGif}
            />
          </div>

          {/* Visitor Detail Drawer - desktop only */}
          <VisitorDetailDrawer
            visitor={conversation.visitor}
            open={visitorDrawerOpen}
            onOpenChange={setVisitorDrawerOpen}
          />
        </div>
      </MainLayout>
    </>
  )
}

export default function Conversation({
  current_user,
  current_account,
  inbox,
  conversation,
  messages,
  conversations,
  conversations_next_cursor = null,
  current_filter = 'unresolved',
  current_folder = 'inbox',
  current_assignment = 'everyone',
  team_members = []
}: ConversationProps) {
  return (
    <FilterProvider currentFilter={current_filter} currentFolder={current_folder} currentAssignment={current_assignment}>
      <ConversationContent
        current_user={current_user}
        current_account={current_account}
        inbox={inbox}
        conversation={conversation}
        messages={messages}
        conversations={conversations}
        conversations_next_cursor={conversations_next_cursor}
        team_members={team_members}
      />
    </FilterProvider>
  )
}
